import { useAtom } from 'jotai';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { PUBLIC_PAGES_IDS, DISABLED_NOTIFICATION_PAGES_IDS } from '../constants';
import { notificationAtom } from '../jotai-atoms/notification.atom';
import { reservationDataAtom } from '../jotai-atoms/reservation-data.atom';
import { userAtom } from '../jotai-atoms/user.atom';
import { NotificationService } from '../services/notification';
import { AnnouncementModal } from '../stories/AnnouncementModal';
import { ModalLiveClassCancelledBySystem } from '../stories/ModalLiveClassCancelledBySystem';
import { ModalLiveClassCancelledNoCredits } from '../stories/ModalLiveClassCancelledNoCredits';
import { ModalLiveClassCancelledTnp } from '../stories/ModalLiveClassCancelledTnp';
import { ModalLiveClassMissed } from '../stories/ModalLiveClassMissed';
import { ModalMigratedStudent } from '../stories/ModalMigratedStudent';
import { ModalReservationBooked } from '../stories/ModalReservationBooked';
import { ModalReservationConfirm } from '../stories/ModalReservationConfirm';
import ModalSurvey from '../stories/ModalSurvey';

interface NotificationProviderProps {
  children: React.ReactNode;
}

interface NotificationContextData {
  checkTimeoutInSeconds: number;
  setCheckTimeoutInSeconds: (seconds: number) => void;
}

type NotificationModal =
  | 'liveclass_survey'
  | 'liveclass_reserved'
  | 'liveclass_confirm_presence'
  | 'liveclass_missed'
  | 'liveclass_cancelled_tnp'
  | 'liveclass_cancelled_by_system'
  | 'liveclass_cancelled_nocredit'
  | 'student_program_migrated';

const NotificationContext = createContext<NotificationContextData>({} as NotificationContextData);

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [user, setUser] = useAtom(userAtom);
  const [notification, setNotification] = useAtom(notificationAtom);
  const [reservationData, setReservationData] = useAtom(reservationDataAtom);

  const [activeModal, setActiveModal] = useState<NotificationModal | null>(null);
  const [checkTimeoutInSeconds, setCheckTimeoutInSeconds] = useState<number>(30);
  const availableNotifications: NotificationModal[] = [
    'liveclass_survey',
    'liveclass_reserved',
    'liveclass_confirm_presence',
    'liveclass_missed',
    'liveclass_cancelled_tnp',
    'liveclass_cancelled_by_system',
    'liveclass_cancelled_nocredit',
    'student_program_migrated',
  ];

  const checkNotificationTimeOut = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    checkNotification();

    return () => {
      clearTimeout(checkNotificationTimeOut.current as NodeJS.Timeout);
    };
  }, [reservationData?.forceCheckNotification]);

  const checkNotification = async () => {
    if (!isNotificationCheckerEnabled()) {
      return;
    }

    try {
      const response = await NotificationService.getNextNotificationForTheCurrentUser();
      const hasNotification = !!response?.data?.notification?.data;

      if (hasNotification) {
        const notification = response.data.notification;
        setNotification(notification);

        if (notification?.type && availableNotifications.includes(notification.type)) {
          setActiveModal(notification.type);
        }
      } else {
        setActiveModal(null);
        setNotification(null);
      }
    } catch {
      // Ignore errors while trying to get notifications
    }

    clearTimeout(checkNotificationTimeOut.current as NodeJS.Timeout);
    checkNotificationTimeOut.current = setTimeout(function () {
      checkNotification();
    }, checkTimeoutInSeconds * 1000);
  };

  const handleCloseAnnouncementModal = () => {
    if (!user) {
      return;
    }
    setUser({ ...user, announcement: undefined });
  };

  const markNotificationAsRead = () => {
    if (!notification) {
      return;
    }
    NotificationService.putNotificationRead(notification.data.notificationId);
    setNotification(null);
  };

  return (
    <NotificationContext.Provider
      value={{
        checkTimeoutInSeconds,
        setCheckTimeoutInSeconds,
      }}
    >
      <ModalSurvey show={activeModal === 'liveclass_survey'} onClose={() => setActiveModal(null)} />
      <ModalMigratedStudent
        show={activeModal === 'student_program_migrated'}
        onClose={() => {
          setActiveModal(null);
          markNotificationAsRead();
        }}
      />
      <ModalReservationBooked
        show={activeModal === 'liveclass_reserved'}
        onClose={() => {
          setActiveModal(null);
          markNotificationAsRead();
          setReservationData({
            ...reservationData,
            forceRefreshUserData: !reservationData?.forceRefreshUserData,
          });
        }}
        isGameClass={reservationData?.selectedLiveClass?.type === 'gameclass'}
      />
      <ModalReservationConfirm
        show={activeModal === 'liveclass_confirm_presence'}
        onClose={() => setActiveModal(null)}
        liveClassId={notification?.data.liveClassId}
        isGameClass={notification?.data?.liveClassType === 'GAME_KAHOOT'}
      />
      <ModalLiveClassMissed
        show={activeModal === 'liveclass_missed'}
        onClose={() => setActiveModal(null)}
      />
      <ModalLiveClassCancelledTnp
        show={activeModal === 'liveclass_cancelled_tnp'}
        onClose={() => setActiveModal(null)}
      />
      <ModalLiveClassCancelledBySystem
        show={activeModal === 'liveclass_cancelled_by_system'}
        onClose={() => setActiveModal(null)}
      />
      <ModalLiveClassCancelledNoCredits
        show={activeModal === 'liveclass_cancelled_nocredit'}
        onClose={() => setActiveModal(null)}
      />
      {children}
      <AnnouncementModal
        title={user?.announcement?.title}
        body={user?.announcement?.body}
        onClose={handleCloseAnnouncementModal}
      />
    </NotificationContext.Provider>
  );
};

export function useNotification(): NotificationContextData {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotification must be used within an NotificationContext');
  }

  return context;
}

const isNotificationCheckerEnabled = () => {
  const pageId = document.getElementById('root')?.getAttribute('page-id') || '';
  return !(PUBLIC_PAGES_IDS.includes(pageId) || DISABLED_NOTIFICATION_PAGES_IDS.includes(pageId));
};
